import React from 'react';

interface LegalNoticeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContactClick?: () => void;
}

const LegalNoticeModal: React.FC<LegalNoticeModalProps> = ({ isOpen, onClose, onContactClick }) => {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        style={{ backgroundColor: '#161616' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-white">Mentions Légales</h2>
            <p className="text-gray-400 text-sm mt-1">Dernière mise à jour : 9 janvier 2026</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors text-2xl"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-6 text-gray-300">
          
          {/* Section 1: Éditeur */}
          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">1. Éditeur du site</h3>
            <p className="mb-3"><strong className="text-cyan-400">Chord Notator</strong> est édité par :</p>
            <div className="bg-gray-800/50 p-4 rounded-lg space-y-2">
              <p><strong className="text-gray-200">Nom :</strong> Pascal GANDOLFO</p>
              <p><strong className="text-gray-200">Statut :</strong> Entrepreneur individuel</p>
              <p><strong className="text-gray-200">SIREN :</strong> 447 869 538</p>
              <p><strong className="text-gray-200">SIRET :</strong> 447 869 538 00051</p>
              <p><strong className="text-gray-200">RCS :</strong> Marseille</p>
              <p><strong className="text-gray-200">Code APE :</strong> 6201Z - Programmation informatique</p>
              <p><strong className="text-gray-200">Adresse :</strong> Résidence L'Espigoulier Bat A, 97 Avenue de la Glacière, 13390 Auriol</p>
              <p><strong className="text-gray-200">Email :</strong>{' '}
                <button
                  onClick={() => {
                    onContactClick?.();
                    onClose();
                  }}
                  className="text-cyan-400 hover:text-cyan-300 underline"
                >
                  pascalgandolfo@gmail.com
                </button>
              </p>
            </div>
            <p className="mt-3 text-sm text-gray-400 italic">TVA non applicable, article 293 B du Code Général des Impôts</p>
          </section>

          {/* Section 2: Directeur de publication */}
          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">2. Directeur de la publication</h3>
            <p><strong className="text-gray-200">Nom :</strong> Pascal GANDOLFO</p>
          </section>

          {/* Section 3: Hébergement */}
          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">3. Hébergement</h3>
            <p className="mb-2">Le site Chord Notator est hébergé par :</p>
            <div className="bg-gray-800/50 p-4 rounded-lg space-y-1">
              <p><strong className="text-gray-200">Vercel Inc.</strong></p>
              <p>440 N Barranca Ave #4133</p>
              <p>Covina, CA 91723, États-Unis</p>
              <p>Site web : <a href="https://vercel.com" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 underline">https://vercel.com</a></p>
            </div>
          </section>

          {/* Section 4: Propriété intellectuelle */}
          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">4. Propriété intellectuelle</h3>
            
            <h4 className="text-lg font-semibold text-gray-200 mt-4 mb-2">4.1 Contenu du site</h4>
            <p className="mb-3">L'ensemble des éléments constituant le site Chord Notator (textes, graphismes, logiciels, images, logos, marques, bases de données, etc.) relèvent des législations françaises et internationales sur le droit d'auteur et la propriété intellectuelle.</p>
            <p className="mb-3">Ces éléments sont la propriété exclusive de Pascal GANDOLFO. Toute reproduction, représentation, modification ou utilisation non autorisée est interdite.</p>
            
            <h4 className="text-lg font-semibold text-gray-200 mt-4 mb-2">4.2 Contenu utilisateur</h4>
            <p>Les grilles d'accords, setlists et autres contenus créés par les utilisateurs restent leur propriété intellectuelle. Chord Notator n'acquiert aucun droit de propriété sur ces contenus.</p>
          </section>

          {/* Section 5: Protection des données */}
          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">5. Protection des données personnelles</h3>
            
            <h4 className="text-lg font-semibold text-gray-200 mt-4 mb-2">5.1 Responsable du traitement</h4>
            <p className="mb-3">Pascal GANDOLFO - pascalgandolfo@gmail.com</p>
            
            <h4 className="text-lg font-semibold text-gray-200 mt-4 mb-2">5.2 Données collectées</h4>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-gray-200">Données d'identification :</strong> nom, prénom, adresse email</li>
              <li><strong className="text-gray-200">Données de connexion :</strong> logs de connexion, adresse IP</li>
              <li><strong className="text-gray-200">Données d'utilisation :</strong> grilles d'accords créées, setlists, préférences</li>
              <li><strong className="text-gray-200">Données de paiement :</strong> gérées exclusivement par Stripe</li>
            </ul>

            <h4 className="text-lg font-semibold text-gray-200 mt-4 mb-2">5.3 Vos droits (RGPD)</h4>
            <p className="mb-2">Vous disposez des droits suivants :</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Droit d'accès à vos données</li>
              <li>Droit de rectification</li>
              <li>Droit à l'effacement</li>
              <li>Droit à la portabilité</li>
              <li>Droit d'opposition</li>
            </ul>
            <p className="mt-3">Pour exercer vos droits :{' '}
              <button
                onClick={() => {
                  onContactClick?.();
                  onClose();
                }}
                className="text-cyan-400 hover:text-cyan-300 underline"
              >
                Contactez-nous
              </button>
            </p>

            <h4 className="text-lg font-semibold text-gray-200 mt-4 mb-2">5.4 Réclamation CNIL</h4>
            <p>Vous pouvez introduire une réclamation auprès de la CNIL :</p>
            <p className="text-sm text-gray-400 mt-1">Commission Nationale de l'Informatique et des Libertés<br/>3 Place de Fontenoy - TSA 80715, 75334 Paris Cedex 07<br/>
              <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 underline">www.cnil.fr</a>
            </p>
          </section>

          {/* Section 6: Cookies */}
          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">6. Cookies</h3>
            <p className="mb-2">Chord Notator utilise des cookies pour :</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong className="text-gray-200">Cookies essentiels :</strong> authentification, session utilisateur</li>
              <li><strong className="text-gray-200">Cookies de performance :</strong> analyse d'utilisation (anonymisée)</li>
            </ul>
            <p className="mt-3 text-sm text-gray-400">Vous pouvez configurer votre navigateur pour refuser les cookies.</p>
          </section>

          {/* Section 7: Limitation de responsabilité */}
          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">7. Limitation de responsabilité</h3>
            <p className="mb-2">Chord Notator s'efforce d'assurer la disponibilité du service 24h/24 et 7j/7. Toutefois, l'accès peut être interrompu pour maintenance ou cas de force majeure.</p>
            <p>Pascal GANDOLFO ne saurait être tenu responsable des contenus créés par les utilisateurs.</p>
          </section>

          {/* Section 8: Droit applicable */}
          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">8. Droit applicable et juridiction</h3>
            <p>Les présentes mentions légales sont soumises au droit français. En cas de litige, les tribunaux français seront seuls compétents.</p>
          </section>

          {/* Section 9: Contact */}
          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">9. Contact</h3>
            <p>Pour toute question concernant ces mentions légales :</p>
            <p className="mt-2">
              <button
                onClick={() => {
                  onContactClick?.();
                  onClose();
                }}
                className="text-cyan-400 hover:text-cyan-300 underline"
              >
                pascalgandolfo@gmail.com
              </button>
            </p>
          </section>

        </div>

        {/* Footer */}
        <div className="border-t border-gray-700 p-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg font-semibold transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default LegalNoticeModal;
